import { z } from 'zod';
import { PALLET_STATES } from '../domain/pallet-state.js';
const locationLabel = z.string().trim().max(8);
export const transferPalletContentSchema = z
    .object({
    sourcePalletId: z.string().uuid(),
    skuId: z.string().uuid(),
    qty: z.number().int().positive().max(1_000_000),
    reason: z.string().trim().min(1).max(500),
})
    .strict();
export const palletContentLineSchema = z.object({
    skuId: z.string().uuid(),
    qty: z.number().positive().max(1_000_000),
});
/**
 * Single-pallet location triplet — used by the multi-pallet receive form so
 * each physical pallet gets its own row/shelf/position.
 */
const palletLocationSchema = z.object({
    locationRow: locationLabel.optional(),
    locationShelf: locationLabel.optional(),
    locationPosition: locationLabel.optional(),
    /** Contents of this exact physical pallet in a multi-pallet receipt. */
    contents: z.array(palletContentLineSchema).min(1).max(200).optional(),
});
export const createPalletSchema = z
    .object({
    /**
     * Pallet owner — exactly one of `customerId` / `ownerPartnerId` must be
     * set. A storage customer owns a billable storage pallet; a partner owns
     * an assembly-side pallet (code P-{PartnerCode}-…).
     */
    customerId: z.string().uuid().optional(),
    ownerPartnerId: z.string().uuid().optional(),
    /**
     * Partner the pallet's contents are destined for (optional). Set at
     * receive time so the partner can see incoming goods before they're
     * unpacked into stock. Only meaningful for customer-owned pallets.
     */
    destinationPartnerId: z.string().uuid().optional(),
    /** "expected" or "arrived" — the only legal initial states. */
    initialState: z.enum(['EXPECTED', 'ARRIVED']).default('EXPECTED'),
    expectedAt: z.coerce.date().optional(),
    arrivedAt: z.coerce.date().optional(),
    comment: z.string().trim().max(2000).optional(),
    weightKg: z.number().positive().max(100_000).optional(),
    /**
     * Legacy single-pallet path: a `count` ≥ 1 with one (shared) location.
     * Kept for backwards compatibility and for the "we don't care which spot
     * each pallet sits in" case.
     */
    count: z.number().int().min(1).max(1000).default(1),
    locationRow: locationLabel.optional(),
    locationShelf: locationLabel.optional(),
    locationPosition: locationLabel.optional(),
    /** Optional contents of the single physical pallet. May be added at unpack. */
    contents: z.array(palletContentLineSchema).min(1).max(200).optional(),
    /**
     * Multi-pallet receive (2026-05-28 customer feedback). When provided,
     * the service creates one Pallet row per entry, each with count=1, each
     * with its own location. `count`, `locationRow`, `locationShelf`,
     * `locationPosition` are ignored when `locations` is set.
     */
    locations: z.array(palletLocationSchema).min(1).max(1000).optional(),
})
    .superRefine((data, ctx) => {
    if (data.locations?.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['locations'],
            message: 'locations[] must contain at least one entry when provided',
        });
    }
    // Exactly one owner: a storage customer or a partner.
    if (!data.customerId === !data.ownerPartnerId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customerId'],
            message: 'exactly one of customerId / ownerPartnerId must be set',
        });
    }
    const partnerReceipt = Boolean(data.ownerPartnerId ?? data.destinationPartnerId);
    const suppliedContents = data.locations
        ? data.locations.some((location) => Boolean(location.contents?.length))
        : Boolean(data.contents?.length);
    if (!partnerReceipt && suppliedContents) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: data.locations ? ['locations'] : ['contents'],
            message: 'pallet contents require an owner or destination partner',
        });
    }
});
export const updatePalletLocationSchema = z.object({
    locationRow: locationLabel.nullable().optional(),
    locationShelf: locationLabel.nullable().optional(),
    locationPosition: locationLabel.nullable().optional(),
});
export const palletStateSchema = z.enum(PALLET_STATES);
export const transitionPalletSchema = z.object({
    toState: palletStateSchema,
    /** Required since 2026-05-28: operators must justify every transition. */
    reason: z.string().trim().min(1).max(2000),
    /** Optional photo evidence object key (already uploaded to MinIO). */
    photoKey: z.string().trim().max(512).optional(),
    /** Effective date of the operation (WMS-16). Defaults to now when omitted. */
    date: z.coerce.date().optional(),
});
/**
 * Apply one state operation to several pallets atomically. `DEPLETED` is the
 * warehouse-list status used for an exhausted pallet; the API maps it to the
 * existing confirmed-empty DISPATCHED transition and records `depletedAt`.
 */
export const bulkTransitionPalletsSchema = transitionPalletSchema
    .omit({ photoKey: true, toState: true })
    .extend({
    palletIds: z.array(z.string().uuid()).min(2).max(100),
    toState: z.union([palletStateSchema, z.literal('DEPLETED')]),
})
    .superRefine((data, ctx) => {
    if (new Set(data.palletIds).size !== data.palletIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['palletIds'],
            message: 'palletIds must be unique',
        });
    }
});
export const repackPalletSchema = z.object({
    /** Required reason for the parent's REPACKED transition. */
    reason: z.string().trim().min(1).max(2000),
    /** Optional initial location for the freshly issued child pallet. */
    locationRow: locationLabel.optional(),
    locationShelf: locationLabel.optional(),
    locationPosition: locationLabel.optional(),
    /** Optional comment to attach to the child pallet. */
    childComment: z.string().trim().max(2000).optional(),
    /** Effective date of the repack (WMS-16). Defaults to now when omitted. */
    date: z.coerce.date().optional(),
});
export const consolidatePalletSchema = z.object({
    sourcePalletIds: z.array(z.string().uuid()).min(2).max(100),
    lines: z
        .array(z.object({
        sourcePalletId: z.string().uuid(),
        skuId: z.string().uuid(),
        qty: z.number().positive().max(1_000_000),
    }))
        .min(1)
        .max(1000),
    reason: z.string().trim().min(1).max(2000),
    locationRow: locationLabel.optional(),
    locationShelf: locationLabel.optional(),
    locationPosition: locationLabel.optional(),
    childComment: z.string().trim().max(2000).optional(),
    date: z.coerce.date().optional(),
});
export const confirmPalletDepletedSchema = z.object({
    reason: z.string().trim().min(1).max(2000),
    date: z.coerce.date().optional(),
});
/** Move selected posted goods off a partner pallet into the virtual Shelf. */
export const movePalletToShelfSchema = z.object({
    lines: z
        .array(z.object({
        skuId: z.string().uuid(),
        qty: z.number().positive().max(1_000_000),
    }))
        .min(1)
        .max(200)
        .superRefine((lines, ctx) => {
        const seen = new Set();
        lines.forEach((line, index) => {
            if (seen.has(line.skuId))
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [index, 'skuId'],
                    message: 'duplicate sku',
                });
            seen.add(line.skuId);
        });
    }),
    reason: z.string().trim().min(1).max(2000),
});
/** Put virtual Shelf stock on an eligible partner pallet, or create one. */
export const moveShelfToPalletSchema = z.object({
    partnerId: z.string().uuid(),
    skuId: z.string().uuid(),
    qty: z.number().int().positive().max(1_000_000),
    target: z.discriminatedUnion('type', [
        z.object({ type: z.literal('EXISTING'), palletId: z.string().uuid() }),
        z.object({
            type: z.literal('NEW'),
            locationRow: locationLabel.optional(),
            locationShelf: locationLabel.optional(),
            locationPosition: locationLabel.optional(),
        }),
    ]),
    reason: z.string().trim().min(1).max(2000),
});
export const adjustPalletSchema = z.object({
    /** Required for the audit log per TRS §5.6. */
    reason: z.string().trim().min(1).max(2000),
    comment: z.string().trim().max(2000).optional(),
    weightKg: z.number().positive().max(100_000).nullable().optional(),
    count: z.number().int().min(1).max(1000).optional(),
    expectedAt: z.coerce.date().nullable().optional(),
    locationRow: locationLabel.nullable().optional(),
    locationShelf: locationLabel.nullable().optional(),
    locationPosition: locationLabel.nullable().optional(),
});
export const palletPriceOverrideSchema = z.object({
    periodFrom: z.coerce.date(),
    periodTo: z.coerce.date(),
    mode: z.enum(['FLAT_RATE', 'FLAT_AMOUNT']),
    valueCents: z.number().int().min(0),
    reason: z.string().trim().min(1).max(2000),
});
//# sourceMappingURL=pallet.js.map